import type { AftercareEvent, AftercareRecord, Project } from './project';
import { getPack } from './packs/registry';

export const aftercareCategoryLabel:Record<AftercareRecord['category'],string> = {
  damage:'손상·오염',
  missing_item:'누락·분실 확인',
  additional_charge:'추가 비용 확인',
  unfinished_work:'미완료 작업',
  other:'그 밖의 확인'
};

export const aftercareStatusLabel:Record<AftercareRecord['status'],string> = {
  recorded:'기록함',
  request_sent:'확인 요청 보냄',
  reply_received:'회신 받음',
  closed:'마감함'
};

const conclusionPattern = /(?:사기(?:꾼|업체)?|불법(?:이다|업체)|위법(?:이다|확정)|배상\s*책임(?:이\s*있다|확정)|배상해야|(?:업체\s*)?과실(?:이)?\s*(?:확실|명백|있다)|전적으로\s*(?:잘못|과실)|무조건\s*(?:환불|배상)|고의로\s*(?:파손|누락))/;

export function isSafeAftercareText(value:string) {
  return !conclusionPattern.test(value.replace(/\s+/g,' ').trim());
}

export function recordCountBucket(count:number) {
  if (count <= 1) return '1';
  if (count <= 3) return '2_3';
  return '4_plus';
}

export function transitionAftercare(
  record:AftercareRecord,
  status:AftercareRecord['status'],
  text:string,
  occurredAt=new Date().toISOString()
):AftercareRecord {
  const kind:AftercareEvent['kind'] = status === 'recorded' && record.status === 'closed'
    ? 'reopened'
    : status;
  return {
    ...record,
    status,
    events:[...record.events,{id:crypto.randomUUID(),kind,text:text.trim(),occurredAt}],
    updatedAt:occurredAt
  };
}

export function aftercareSummary(records:AftercareRecord[]) {
  return {
    total:records.length,
    open:records.filter((record)=>record.status!=='closed').length,
    sent:records.filter((record)=>record.status==='request_sent').length,
    replied:records.filter((record)=>record.status==='reply_received').length,
    closed:records.filter((record)=>record.status==='closed').length,
    photoCount:records.reduce((total,record)=>total+record.evidencePhotos.length,0)
  };
}

function itemLabel(project:Project,itemId:string) {
  return getPack(project.packId).checklist.find((item)=>item.id===itemId)?.label??itemId;
}

export function buildAftercareFactPacket(project:Project, createdAt=new Date().toISOString()) {
  const records=[...project.aftercare].sort((left,right)=>left.occurredAt.localeCompare(right.occurredAt));
  const header=[
    '적힌대로 · 이사 후 사실 확인 묶음',
    `기록: ${project.title}`,
    `이사일: ${project.moveDate ?? '미정'}`,
    `정리 시각: ${new Date(createdAt).toLocaleString('ko-KR')}`,
    '',
    '이 문서는 사용자가 관찰한 사실과 확인 요청을 시간순으로 정리한 자료입니다. 적힌대로는 손상 원인, 귀책, 위법, 배상 또는 환불 여부를 판단하지 않습니다.'
  ];
  const rows=records.flatMap((record,index)=>[
    '',
    `[${index+1}] ${aftercareCategoryLabel[record.category]} · ${record.subject}`,
    `- 확인 시각: ${new Date(record.occurredAt).toLocaleString('ko-KR')}`,
    `- 현재 상태: ${aftercareStatusLabel[record.status]}`,
    `- 연결 항목: ${record.relatedItemIds.length?record.relatedItemIds.map((id)=>itemLabel(project,id)).join(', '):'직접 기록'}`,
    `- 관찰한 사실: ${record.observedFacts}`,
    `- 확인 요청: ${record.requestedAction}`,
    `- 확인된 금액: ${record.confirmedAmountKrw===null?'기록 없음':`${record.confirmedAmountKrw.toLocaleString()}원`}`,
    `- 기기 보관 사진: ${record.evidencePhotos.length}장 (파일은 자동 첨부되지 않음)`,
    ...record.events.map((event)=>`- ${new Date(event.occurredAt).toLocaleString('ko-KR')} · ${event.kind}: ${event.text||'상태 변경'}`)
  ]);
  return [...header,...project.contractNotices.filter(n=>n.kind==='no_show').flatMap(n=>['',`노쇼·연락 안 됨 · ${n.receivedAt}`,n.originalMessage,...(n.contactAttempts??[])]),...project.settlements.filter(r=>r.demandedOnSite).map(r=>`현장 요구 · ${r.occurredOn} · ${r.note} · ${r.amountKrw===null?'금액 안 적힘':`${r.amountKrw.toLocaleString()}원`}`),...rows,'','사진 원본과 계약서 원문은 사용자가 직접 확인해 필요한 경우 별도로 첨부해야 합니다.'].join('\n');
}
